import { Injectable, inject } from '@angular/core'
import {BehaviorSubject, distinctUntilChanged} from "rxjs";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {LocaleService} from './services/locale-service/locale.service';
import {ServiceProviderHub} from '@generated-backend-types';
import {Breadcrumb} from './entities/breadcrumb.entity';
import MultilingualAttributeData = ServiceProviderHub.App.Data.Meta.MultilingualAttributeData;

@Injectable({providedIn: 'root'})
export class BreadcrumbService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private localeService = inject(LocaleService);

  breadcrumbs$: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject<Breadcrumb[]>([]);

  constructor() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const breadcrumbs = this.buildBreadcrumbs(this.route.root);
        this.breadcrumbs$.next(breadcrumbs);
      });

    this.localeService.localeChanged.pipe(distinctUntilChanged()).subscribe(() => {
      const breadcrumbs = this.buildBreadcrumbs(this.route.root);
      this.breadcrumbs$.next(breadcrumbs);
    });
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    parentUrl: string = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) return breadcrumbs;

    for (const child of children) {
      const routeSnapshot = child.snapshot;

      // Build the segment path with params if any
      const segments = routeSnapshot.url.map(segment => {
        if (segment.path.startsWith(':')) {

          const paramName = segment.path.slice(1);

          return routeSnapshot.params[paramName];
        }
        return segment.path;
      });

      const currentSegment = segments.join('/');

      const fullUrl = currentSegment ? `${parentUrl}/${currentSegment}` : parentUrl;

      const rawBreadcrumbs: Breadcrumb[] = (routeSnapshot.data['breadcrumbs'] || routeSnapshot.data['breadcrumb']) ?? [];
      const breadcrumbArray = Array.isArray(rawBreadcrumbs) ? rawBreadcrumbs : [rawBreadcrumbs];

      for (const bc of breadcrumbArray) {
        let label = '';

        if (bc.dynamic && bc.dataKey) {
          for (const dataKey of bc.dataKey.split(',')) {
            const value = this.getValueByPath(routeSnapshot.data, dataKey);

            if (value && typeof value === 'object' && 'en' in value && 'de' in value) {
              label = label + ' ' + ((value as MultilingualAttributeData)[this.localeService.currentLocale] ?? bc.label);
            } else {
              label = label + ' ' + (typeof value === 'string' ? value : bc.label);
            }
          }

          label = label.trim();

        } else {
          label = bc.label;
        }

        let route = null;
        if (bc.route) {
          const segments: string[] = bc.route.split('/').map(
            (segment: string) => {
              if (
                segment.startsWith(':')
              ) {
                const paramName = segment.slice(1);
                // Params like :id are not inherited past component routes, so take them from the nearest ancestor that has them.
                return routeSnapshot.pathFromRoot.map(r => r.params[paramName]).reverse().find(value => value !== undefined);
              }
              return segment;
            }
          )
          route = segments.join('/');
        }

        breadcrumbs.push({
          label,
          route: route ?? undefined,
          translate: !bc.dynamic && bc.translate !== false
        });
      }

      return this.buildBreadcrumbs(child, fullUrl, breadcrumbs);
    }

    return breadcrumbs;
  }


  private getValueByPath(data: unknown, path: string): unknown {
    return path.split('.').reduce(
      (acc: unknown, part: string) => {
        // @ts-expect-error indirect any
        return (acc && typeof acc === 'object' && part in acc ? acc[part] : acc)
      }, data
    );
  }
}
